import { useEffect, useState } from 'react'

import { Lookup, useLookup } from '@/providers'
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog'
import DrawerHeader from '@components/DrawerHeader/DrawerHeader'
import {
	Button,
	Drawer,
	InputAdornment,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import { useDialogs } from '@toolpad/core'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { BooleanParam, useQueryParam } from 'use-query-params'

import { useRemoveBlanketMarkup, useUpdateBlanketMarkup } from './BlanketMarkup.hooks'

export default function BlanketMarkup() {
	const [drawerVisible, setDrawerVisible] = useQueryParam('blanket-markup', BooleanParam)
	const { marketBasket } = useMarketBasketContext()
	const { blanketMarkupAmount, blanketMarkupType, id: marketBasketId } = marketBasket

	const { mutate: applyBlanketMarkup } = useUpdateBlanketMarkup(String(marketBasketId))
	const { mutate: removeBlanketMarkup } = useRemoveBlanketMarkup(String(marketBasketId))

	// Only render when the drawer is open
	if (!drawerVisible) return null

	const handleSave = (markup: { markupType: string; markupAmount: number }) => {
		applyBlanketMarkup({ id: String(marketBasketId), ...markup })
		setDrawerVisible(undefined)
	}

	const handleRemove = () => {
		removeBlanketMarkup(String(marketBasketId))
		setDrawerVisible(undefined)
	}

	const mode = blanketMarkupType === undefined || blanketMarkupType === null ? 'ADD' : 'EDIT'

	return (
		<Drawer
			open={drawerVisible}
			variant="temporary"
			anchor="right"
			onClose={() => setDrawerVisible(undefined)}
			data-testid="blanket-markup-drawer"
			sx={{ '& .MuiDrawer-paper': { width: '29rem' }, zIndex: (theme) => theme.zIndex.modal + 1 }}
		>
			<BlanketMarkupUi
				mode={mode}
				blanketMarkupAmount={blanketMarkupAmount}
				blanketMarkupType={blanketMarkupType}
				onSave={handleSave}
				onRemove={handleRemove}
				onCancel={() => setDrawerVisible(undefined)}
			/>
		</Drawer>
	)
}

type BlanketMarkupUiProps = {
	mode: 'ADD' | 'EDIT'
	blanketMarkupAmount?: number
	blanketMarkupType?: string
	onSave?: (markup: { markupType: string; markupAmount: number }) => void
	onRemove?: () => void
	onCancel: () => void
}

const titlesByMode: Record<BlanketMarkupUiProps['mode'], string> = {
	ADD: 'Apply a blanket markup',
	EDIT: 'Edit blanket markup',
}

export function BlanketMarkupUi({
	mode,
	blanketMarkupAmount = null,
	blanketMarkupType = null,
	onSave = undefined,
	onCancel,
	onRemove = undefined,
}: BlanketMarkupUiProps) {
	const markupTypes = useLookup(Lookup.MarkupTypes)
	const dialogs = useDialogs()

	const [markupType, setMarkupType] = useState<string | undefined>(undefined)
	const [markupAmount, setMarkupAmount] = useState(blanketMarkupAmount?.toString() ?? '')

	useEffect(() => {
		// Default to the existing markup type when editing, otherwise fall back to a percentage markup
		const defaultMarkupTypeId = blanketMarkupType ?? 'PERCENT_MARKUP'
		setMarkupType(defaultMarkupTypeId)
	}, [markupTypes, blanketMarkupType])

	const handleSave = () => {
		if (!markupType || markupAmount === '') return
		onSave?.({ markupType: String(markupType), markupAmount: Number(markupAmount) })
	}

	const handleRemove = async () => {
		const confirmed = await dialogs.open(ConfirmDialog, {
			title: 'Remove Blanket Markup',
			description:
				'This will remove the blanket markup applied to this basket and revert to the contract markup. This cannot be undone.',
			confirmLabel: 'Remove',
			rejectLabel: 'Cancel',
			// The drawer's elevated zIndex otherwise hides the dialog behind it
			sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
		})

		if (confirmed) {
			onRemove?.()
		}
	}

	return (
		<Stack spacing={2} sx={{ width: '100%', p: 2 }}>
			<DrawerHeader
				label="Blanket Markup"
				onCancel={onCancel}
				onSave={handleSave}
				dirty={markupType && markupAmount !== ''}
			/>

			<Stack spacing={2} data-testid="blanket-markup-form">
				<Typography variant="h6" data-testid="blanket-markup-form-title">
					{titlesByMode[mode]}
				</Typography>
				<Typography variant="body1" sx={{ pb: 3 }}>
					Overrides the current markup on every match in this basket. Originals are kept so you can
					revert.
				</Typography>

				<TextField
					select
					required
					fullWidth
					name="markupType"
					label="Markup Type"
					data-testid="blanket-markup-type-select"
					value={markupType ?? ''}
					onChange={(event) => setMarkupType(event.target.value)}
					slotProps={{
						select: {
							// The Drawer's elevated zIndex otherwise hides the menu behind it
							MenuProps: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } },
						},
					}}
				>
					{markupTypes.map((type) => (
						<MenuItem
							key={type.id}
							value={type.id}
							data-testid={`blanket-markup-type-option-${type.id}`}
						>
							{type.label}
						</MenuItem>
					))}
				</TextField>

				<TextField
					required
					fullWidth
					type="number"
					name="markupAmount"
					label="Markup"
					value={markupAmount}
					onChange={(event) => setMarkupAmount(event.target.value)}
					slotProps={{
						input:
							markupType === 'FEE_PER_CASE'
								? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
								: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
						htmlInput: {
							step: 'any',
							'data-testid': 'blanket-markup-amount-input',
						},
					}}
				/>

				{mode === 'EDIT' && (
					<Stack direction="row" alignItems="center" sx={{ pt: 2 }}>
						<Button
							color="error"
							variant="outlined"
							data-testid="blanket-markup-remove-button"
							onClick={handleRemove}
							sx={{ width: 'fit-content' }}
						>
							Remove Markup
						</Button>
						<Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
							Removes the existing blanket markup and reverts to the contract markup.
						</Typography>
					</Stack>
				)}
			</Stack>
		</Stack>
	)
}
